import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Breadcrumbs,
  Link,
  Stack,
  Chip,
  IconButton,
  Tooltip,
  Divider,
  LinearProgress,
} from '@mui/material';
import {
  NavigateNext,
  Home,
  Share,
  GitHub,
  ContentCopy,
  CheckCircle,
} from '@mui/icons-material';
import { Link as RouterLink, useParams, Navigate } from 'react-router-dom';
import DocumentationSidebar from '../components/DocumentationSidebar';
import { 
  getDocumentationPage, 
  getDocumentationSection, 
  getAllDocumentationSections 
} from '../data/documentation';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { useTheme, alpha } from '@mui/material/styles';

const MarkdownComponents = (theme) => ({
  h1: ({ children }) => (
    <Typography 
      variant="h3" 
      component="h1" 
      id={children.toString().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}
      sx={{ 
        mb: 3, 
        mt: 4,
        fontWeight: 600, 
        color: 'text.primary',
        scrollMarginTop: '100px', // For smooth scrolling with fixed header
      }}
    >
      {children}
    </Typography>
  ),
  h2: ({ children }) => (
    <Typography 
      variant="h4" 
      component="h2" 
      id={children.toString().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}
      sx={{ 
        mt: 6, 
        mb: 3, 
        fontWeight: 600, 
        color: 'text.primary',
        scrollMarginTop: '100px',
        borderBottom: '1px solid',
        borderColor: 'divider',
        pb: 1,
      }}
    >
      {children}
    </Typography>
  ),
  h3: ({ children }) => (
    <Typography 
      variant="h5" 
      component="h3" 
      id={children.toString().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}
      sx={{ 
        mt: 4, 
        mb: 2, 
        fontWeight: 600, 
        color: 'text.primary',
        scrollMarginTop: '100px',
      }}
    >
      {children}
    </Typography>
  ),
  h4: ({ children }) => (
    <Typography 
      variant="h6" 
      component="h4" 
      sx={{ 
        mt: 3, 
        mb: 2, 
        fontWeight: 600, 
        color: 'primary.main' 
      }}
    >
      {children}
    </Typography>
  ),
  p: ({ children }) => (
    <Typography 
      variant="body1" 
      sx={{ 
        mb: 2, 
        lineHeight: 1.7, 
        color: 'text.primary' 
      }}
    >
      {children}
    </Typography>
  ),
  strong: ({ children }) => (
    <Typography 
      component="strong" 
      sx={{ 
        fontWeight: 600, 
        color: 'text.primary' 
      }}
    >
      {children}
    </Typography>
  ),
  ul: ({ children }) => (
    <Box component="ul" sx={{ mb: 2, pl: 3, color: 'text.primary' }}>
      {children}
    </Box>
  ),
  ol: ({ children }) => (
    <Box component="ol" sx={{ mb: 2, pl: 3, color: 'text.primary' }}>
      {children}
    </Box>
  ),
  li: ({ children }) => (
    <Typography component="li" sx={{ mb: 0.5, lineHeight: 1.6 }}>
      {children}
    </Typography>
  ),
  blockquote: ({ children }) => (
    <Box
      sx={{
        borderLeft: '4px solid',
        borderColor: 'primary.main',
        backgroundColor: theme.palette.mode === 'dark' 
          ? alpha(theme.palette.primary.main, 0.08)
          : alpha(theme.palette.primary.main, 0.04),
        px: 3,
        py: 2,
        my: 3,
        borderRadius: '0 8px 8px 0',
        '& p:last-child': {
          mb: 0,
        },
      }}
    >
      {children}
    </Box>
  ),
  table: ({ children }) => (
    <Paper elevation={0} sx={{ overflowX: 'auto', mb: 3, border: '1px solid', borderColor: 'divider' }}>
      <Box
        component="table"
        sx={{
          width: '100%',
          borderCollapse: 'collapse',
        }}
      >
        {children}
      </Box>
    </Paper>
  ),
  thead: ({ children }) => (
    <Box component="thead">
      {children}
    </Box>
  ),
  tbody: ({ children }) => (
    <Box component="tbody">
      {children}
    </Box>
  ),
  tr: ({ children }) => (
    <Box component="tr">
      {children}
    </Box>
  ),
  th: ({ children }) => (
    <Box
      component="th"
      sx={{
        backgroundColor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.100',
        color: 'text.primary',
        p: 2,
        fontWeight: 600,
        textAlign: 'left',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      {children}
    </Box>
  ),
  td: ({ children }) => (
    <Box
      component="td"
      sx={{
        p: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
        color: 'text.primary',
      }}
    >
      {children}
    </Box>
  ),
  code: ({ node, inline, className, children, ...props }) => {
    const match = /language-(\\w+)/.exec(className || '');
    const language = match ? match[1] : 'text';
    const codeContent = String(children).replace(/\n$/, '');
    
    return !inline && (match || codeContent.includes('\n')) ? (
      <Box sx={{ my: 4, position: 'relative' }}>
        {/* Enhanced Code Block Container */}
        <Box 
          sx={{ 
            borderRadius: 3,
            overflow: 'hidden',
            backgroundColor: theme.palette.mode === 'dark' ? '#0d1117' : '#f8fafc',
            border: '1px solid',
            borderColor: theme.palette.mode === 'dark' ? 'rgba(56, 139, 253, 0.15)' : 'rgba(13, 110, 253, 0.1)',
            boxShadow: theme.palette.mode === 'dark' 
              ? '0 8px 32px rgba(0, 0, 0, 0.4), 0 1px 3px rgba(0, 0, 0, 0.3)'
              : '0 8px 32px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.06)',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '1px',
              background: theme.palette.mode === 'dark' 
                ? 'linear-gradient(90deg, transparent, rgba(56, 139, 253, 0.3), transparent)'
                : 'linear-gradient(90deg, transparent, rgba(13, 110, 253, 0.2), transparent)',
            }
          }}
        >
          {/* Header Bar */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 3,
              py: 1.5,
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(22, 27, 34, 0.8)' : 'rgba(248, 250, 252, 0.8)',
              borderBottom: '1px solid',
              borderColor: theme.palette.mode === 'dark' ? 'rgba(56, 139, 253, 0.1)' : 'rgba(13, 110, 253, 0.08)',
              backdropFilter: 'blur(8px)',
            }}
          >
            {/* Terminal Dots */}
            <Stack direction="row" spacing={1}>
              <Box sx={{ 
                width: 12, 
                height: 12, 
                borderRadius: '50%', 
                backgroundColor: '#ff5f57' 
              }} />
              <Box sx={{ 
                width: 12, 
                height: 12, 
                borderRadius: '50%', 
                backgroundColor: '#ffbd2e' 
              }} />
              <Box sx={{ 
                width: 12, 
                height: 12, 
                borderRadius: '50%', 
                backgroundColor: '#28ca42' 
              }} />
            </Stack>
            
            {/* Language Badge */}
            <Chip
              label={language.toUpperCase()}
              size="small"
              sx={{
                backgroundColor: theme.palette.mode === 'dark' 
                  ? 'rgba(56, 139, 253, 0.15)' 
                  : 'rgba(13, 110, 253, 0.1)',
                color: theme.palette.mode === 'dark' 
                  ? '#58a6ff' 
                  : '#0969da',
                fontSize: '0.6875rem',
                fontWeight: 600,
                height: 24,
                border: '1px solid',
                borderColor: theme.palette.mode === 'dark' 
                  ? 'rgba(56, 139, 253, 0.2)' 
                  : 'rgba(13, 110, 253, 0.15)',
              }}
            />
          </Box>
          
          {/* Code Content */}
          <Box sx={{ position: 'relative' }}>
            <SyntaxHighlighter
              style={theme.palette.mode === 'dark' ? oneDark : oneLight}
              language={language}
              PreTag="div"
              showLineNumbers={false}
              wrapLines={true}
              wrapLongLines={true}
              customStyle={{
                margin: 0,
                padding: '1.5rem',
                backgroundColor: 'transparent',
                fontSize: '0.875rem',
                lineHeight: 1.7,
                fontFamily: '"Fira Code", "JetBrains Mono", "SF Mono", Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                overflow: 'auto',
                maxHeight: '500px',
              }}
              codeTagProps={{
                style: {
                  fontSize: '0.875rem',
                  fontFamily: '"Fira Code", "JetBrains Mono", "SF Mono", Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                }
              }}
              {...props}
            >
              {codeContent}
            </SyntaxHighlighter>
            
            {/* Copy Button */}
            <IconButton
              size="small"
              onClick={() => navigator.clipboard?.writeText(codeContent)}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                backgroundColor: theme.palette.mode === 'dark' 
                  ? 'rgba(22, 27, 34, 0.8)' 
                  : 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(8px)',
                border: '1px solid',
                borderColor: theme.palette.mode === 'dark' 
                  ? 'rgba(56, 139, 253, 0.2)' 
                  : 'rgba(13, 110, 253, 0.15)',
                color: theme.palette.mode === 'dark' ? '#58a6ff' : '#0969da',
                opacity: 0.7,
                transition: 'all 0.2s ease',
                '&:hover': {
                  opacity: 1,
                  backgroundColor: theme.palette.mode === 'dark' 
                    ? 'rgba(56, 139, 253, 0.1)' 
                    : 'rgba(13, 110, 253, 0.08)',
                  transform: 'scale(1.05)',
                },
              }}
            >
              <ContentCopy sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        </Box>
      </Box>
    ) : (
      // Enhanced Inline Code
      <Box
        component="code"
        sx={{
          backgroundColor: theme.palette.mode === 'dark' 
            ? 'rgba(56, 139, 253, 0.15)' 
            : 'rgba(13, 110, 253, 0.08)',
          color: theme.palette.mode === 'dark' 
            ? '#58a6ff' 
            : '#0969da',
          px: 0.875,
          py: 0.375,
          borderRadius: 1,
          fontSize: '0.875em',
          fontFamily: '"Fira Code", "JetBrains Mono", "SF Mono", Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
          fontWeight: 500,
          border: '1px solid',
          borderColor: theme.palette.mode === 'dark' 
            ? 'rgba(56, 139, 253, 0.2)' 
            : 'rgba(13, 110, 253, 0.15)',
          boxShadow: theme.palette.mode === 'dark' 
            ? '0 1px 3px rgba(0, 0, 0, 0.3)' 
            : '0 1px 3px rgba(0, 0, 0, 0.08)',
        }}
        {...props}
      >
        {children}
      </Box>
    );
  },
  a: ({ children, href, ...props }) => (
    <Link
      href={href}
      target={href?.startsWith('http') ? '_blank' : undefined}
      rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
      sx={{
        color: 'primary.main',
        textDecoration: 'none',
        '&:hover': {
          textDecoration: 'underline',
        },
      }}
      {...props}
    >
      {children}
    </Link>
  ),
});


const CorporateDocumentationPostPage = () => {
  const { section, page } = useParams();
  const theme = useTheme();
  const [readingProgress, setReadingProgress] = useState(0);
  const [copySuccess, setCopySuccess] = useState(false);
  
  // Calculate reading progress - moved before conditional return
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = (scrollTop / docHeight) * 100;
      setReadingProgress(Math.min(scrollPercent, 100));
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const docPage = getDocumentationPage(section, page);
  const docSection = getDocumentationSection(section);
  const allSections = getAllDocumentationSections();

  if (!docPage || !docSection) {
    return <Navigate to="/docs" replace />;
  }

  const currentSectionIndex = allSections.findIndex(s => s.key === section);
  const currentSection = allSections[currentSectionIndex];
  const currentPageIndex = currentSection.pages.indexOf(page);
  
  const nextPage = currentPageIndex < currentSection.pages.length - 1 ? currentSection.pages[currentPageIndex + 1] : null;
  const prevPage = currentPageIndex > 0 ? currentSection.pages[currentPageIndex - 1] : null;

  // Get estimated reading time
  const getReadingTime = (content) => {
    const wordsPerMinute = 200;
    const words = content.split(' ').length;
    return Math.ceil(words / wordsPerMinute);
  };

  const copyPageUrl = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy URL');
    }
  };

  return (
    <>
      {/* Reading Progress Bar */}
      <LinearProgress 
        variant="determinate" 
        value={readingProgress} 
        sx={{ 
          position: 'fixed', 
          top: 64, 
          left: 0, 
          right: 0, 
          zIndex: 1100,
          height: 3,
          backgroundColor: 'transparent',
        }} 
      />
      
      <Box sx={{ backgroundColor: 'background.default', minHeight: '100vh', display: 'flex' }}>
        {/* Left Sidebar - Navigation */}
        <Box 
          sx={{ 
            width: { xs: 0, md: '20%', lg: '16.66%' },
            display: { xs: 'none', md: 'block' },
            position: 'sticky', 
            top: 0, 
            height: '100vh', 
            overflowY: 'auto',
            backgroundColor: theme.palette.mode === 'dark' 
              ? 'rgba(17, 24, 39, 0.4)' 
              : 'rgba(248, 250, 252, 0.6)',
            backdropFilter: 'blur(10px)',
            borderRight: '1px solid',
            borderColor: theme.palette.mode === 'dark' 
              ? 'rgba(255, 255, 255, 0.1)' 
              : 'rgba(0, 0, 0, 0.08)',
            flexShrink: 0,
            // Custom scrollbar styling
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: theme.palette.mode === 'dark' 
                ? 'rgba(255, 255, 255, 0.05)' 
                : 'rgba(0, 0, 0, 0.05)',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: theme.palette.mode === 'dark' 
                ? 'rgba(255, 255, 255, 0.2)' 
                : 'rgba(0, 0, 0, 0.2)',
              borderRadius: '4px',
              '&:hover': {
                backgroundColor: theme.palette.mode === 'dark' 
                  ? 'rgba(255, 255, 255, 0.3)' 
                  : 'rgba(0, 0, 0, 0.3)',
              },
            },
            // Firefox scrollbar styling
            scrollbarWidth: 'thin',
            scrollbarColor: theme.palette.mode === 'dark' 
              ? 'rgba(255, 255, 255, 0.2) rgba(255, 255, 255, 0.05)' 
              : 'rgba(0, 0, 0, 0.2) rgba(0, 0, 0, 0.05)',
          }}
        >
          <DocumentationSidebar />
        </Box>

        {/* Main Content */}
        <Box sx={{ 
          flex: 1,
          minHeight: 'calc(100vh - 64px)',
        }}>
          <Box sx={{ 
            maxWidth: '900px',
            mx: 'auto',
            p: { xs: 2, sm: 3, md: 4, lg: 6 },
          }}>
                {/* Breadcrumbs */}
                <Breadcrumbs 
                  separator={<NavigateNext fontSize="small" />} 
                  sx={{ mb: 4, fontSize: '0.875rem' }}
                >
                  <Link
                    component={RouterLink}
                    to="/"
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      color: 'text.secondary', 
                      textDecoration: 'none',
                      '&:hover': { color: 'primary.main' }
                    }}
                  >
                    <Home sx={{ mr: 0.5, fontSize: 16 }} />
                    Home
                  </Link>
                  <Link
                    component={RouterLink}
                    to="/docs"
                    sx={{ 
                      color: 'text.secondary', 
                      textDecoration: 'none',
                      '&:hover': { color: 'primary.main' }
                    }}
                  >
                    Documentation
                  </Link>
                  <Link
                    component={RouterLink}
                    to={`/docs/${section}/${currentSection.pages[0]}`}
                    sx={{ 
                      color: 'text.secondary', 
                      textDecoration: 'none',
                      '&:hover': { color: 'primary.main' }
                    }}
                  >
                    {docSection.title}
                  </Link>
                  <Typography color="text.primary" sx={{ fontWeight: 500 }}>
                    {docPage.title}
                  </Typography>
                </Breadcrumbs>

                {/* Page Header */}
                <Box sx={{ mb: 4 }}>
                  <Typography 
                    variant="h1" 
                    component="h1" 
                    sx={{ 
                      mb: 2, 
                      fontWeight: 700,
                      fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                      lineHeight: 1.2,
                      letterSpacing: '-0.02em',
                    }}
                  >
                    {docPage.title}
                  </Typography>
                  
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      color: 'text.secondary', 
                      mb: 3, 
                      lineHeight: 1.6,
                      fontSize: { xs: '1rem', md: '1.125rem' },
                      fontWeight: 400,
                    }}
                  >
                    {docPage.description}
                  </Typography>

                  {/* Simplified Meta Info */}
                  <Stack 
                    direction="row" 
                    spacing={2} 
                    alignItems="center"
                    divider={<Divider orientation="vertical" flexItem />}
                    sx={{ mb: 3 }}
                  >
                    <Chip 
                      label={docSection.title} 
                      size="small" 
                      variant="outlined"
                    />
                    <Typography variant="body2" color="text.secondary">
                      {getReadingTime(docPage.content)} min read
                    </Typography>
                    <Stack direction="row" spacing={0.5}>
                      <Tooltip title="Edit on GitHub">
                        <IconButton
                          href={`https://github.com/KiefStudioMA/PWGen-Rust/edit/main/docs/${section}/${page}.md`}
                          target="_blank"
                          size="small"
                        >
                          <GitHub fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={copySuccess ? 'Copied!' : 'Copy link'}>
                        <IconButton 
                          onClick={copyPageUrl} 
                          size="small"
                        >
                          {copySuccess ? <CheckCircle fontSize="small" color="success" /> : <Share fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>
                </Box>

                <Divider sx={{ mb: 4 }} />

                {/* Content */}
                <Box
                  sx={{
                    typography: 'body1',
                    '& > *': {
                      mb: 3,
                    },
                    '& h1, & h2, & h3, & h4, & h5, & h6': {
                      mt: 4,
                      mb: 2,
                    },
                    '& img': {
                      maxWidth: '100%',
                      height: 'auto',
                      borderRadius: 1,
                      my: 3,
                      display: 'block',
                    },
                    '& pre': {
                      my: 3,
                    },
                    '& ul, & ol': {
                      pl: 2,
                    },
                    '& li': {
                      mb: 1,
                    },
                    '& blockquote': {
                      my: 3,
                    },
                    '& table': {
                      my: 3,
                    },
                  }}
                >
                  <ReactMarkdown 
                    components={MarkdownComponents(theme)}
                    remarkPlugins={[remarkGfm]}
                  >
                    {docPage.content}
                  </ReactMarkdown>
                </Box>

                <Divider sx={{ my: 6 }} />
                
                {/* Simplified Navigation Footer */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mt: 4 }}>
                  <Box sx={{ flex: 1 }}>
                    {prevPage && (
                      <Button
                        component={RouterLink}
                        to={`/docs/${section}/${prevPage}`}
                        variant="text"
                        startIcon={<NavigateNext sx={{ transform: 'rotate(180deg)' }} />}
                        sx={{ 
                          justifyContent: 'flex-start',
                          textTransform: 'none',
                          color: 'text.secondary',
                          '&:hover': {
                            color: 'primary.main',
                            backgroundColor: 'action.hover',
                          }
                        }}
                      >
                        <Box sx={{ textAlign: 'left' }}>
                          <Typography variant="caption" display="block" sx={{ opacity: 0.7 }}>
                            Previous
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {prevPage.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                          </Typography>
                        </Box>
                      </Button>
                    )}
                  </Box>
                  <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
                    {nextPage && (
                      <Button
                        component={RouterLink}
                        to={`/docs/${section}/${nextPage}`}
                        variant="text"
                        endIcon={<NavigateNext />}
                        sx={{ 
                          justifyContent: 'flex-end',
                          textTransform: 'none',
                          color: 'primary.main',
                          '&:hover': {
                            backgroundColor: 'primary.main',
                            color: 'primary.contrastText',
                          }
                        }}
                      >
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="caption" display="block" sx={{ opacity: 0.7 }}>
                            Next
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {nextPage.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                          </Typography>
                        </Box>
                      </Button>
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>

      
    </>
  );
};

export default CorporateDocumentationPostPage;